import { useEffect } from 'react';
import ResourcesSection from '@99designs/acquisition-frontend/acquisition/components/CategoryPage/ResourcesSection';
import ReviewsSection from '@99designs/acquisition-frontend/acquisition/components/CategoryPage/ReviewsSection';
import { Box, Button } from '@99designs/emu';
import { colors, maxWidths } from '@99designs/emu/settings';
import { DesignInspirationTabbed } from '@99designs/feature-design-inspiration';
import { CurrencyCode } from '@99designs/i18n';
import { CategoryProductSchema, WebPageSchema } from '@99designs/marketing-ui';
import { trackEvent } from '@99designs/tracking';
import { useTrackCTALinkClicked } from '../../lib/events';
import { getStartingPriceForProduct } from '../../lib/getStartingPriceForProduct';
import { products } from '../../lib/productCardProducts';
import { productLandingGroupKeys } from '../../lib/productLandingGroups';
import PageSection from '../PageSection';
import { FaqSection } from './FAQSection';
import { HeroSection } from './HeroSection';
import HowItWorksSection from './HowItWorksSection';
import { RelatedProductsSection } from './RelatedProductsSection';
import { Why99dSection } from './Why99dSection';
import type { Design } from '@99designs/feature-design-inspiration';
import type { ZendeskLanguage } from '@99designs/routing';
import type { Site } from '@99designs/site-navigation/lib/Site';
import type { ProductGroup } from '../../lib/productLandingGroups';
import type { ProductGroupPrice } from '../../types';
import type { CategoryConfig } from './CategoryConfigProvider';

export interface Props {
    site: Site;
    randomFeaturedDesignerNumber: number;
    category: CategoryConfig;
    inspirationDesigns: Design[];
    productGroupPrices: ProductGroupPrice[];
    productGroup: ProductGroup;
}

export default function CategoryPageLayout({
    site,
    category,
    productGroupPrices,
    inspirationDesigns,
    productGroup,
}: Props) {
    const siteCurrency = site.get('currency') as CurrencyCode;
    const language = site.getLanguageCode();

    const primaryCTARefInspiration = useTrackCTALinkClicked({
        category: category.key,
        ctaLocation: 'inspiration-section',
        ctaButton: 'primary',
    });
    const primaryCTARefResources = useTrackCTALinkClicked({
        category: category.key,
        ctaLocation: 'resources-section',
        ctaButton: 'primary',
    });

    useEffect(() => {
        trackEvent('Viewed Product Page', { category: category.key });
    }, [category.key]);

    return (
        <>
            <WebPageSchema
                name={productGroup.title}
                description={productGroup.description}
                inLanguage={language}
                mainEntityOfPage={`/${category.key}`}
                relatedLink={productLandingGroupKeys.flatMap((k) => [`/${k}`, `/${k}/details`])}
            />
            {productGroup.productSchema ? (
                <CategoryProductSchema {...productGroup.productSchema} />
            ) : null}
            <PageSection id="hero" pageName={category.key}>
                <HeroSection
                    categoryKey={category.key}
                    fromPrice={getStartingPriceForProduct(
                        productGroupPrices,
                        category.key,
                        siteCurrency
                    )}
                    title={productGroup.heroTitle}
                    features={productGroup.features}
                    introduction={productGroup.introduction}
                    heroImage={productGroup.heroImage}
                    breadcrumbLabel={productGroup.breadcrumbLabel}
                    heroCtaAppearance={productGroup.heroCtaAppearance}
                    ctaLink={productGroup.productDetailsRelativeLink}
                />
            </PageSection>

            <PageSection id="why-99designs" pageName={category.key}>
                <Why99dSection
                    title={productGroup.why99dTitle}
                    designerInfo={productGroup.why99dDesignerInfo}
                    categoryKey={category.key}
                    language={language as ZendeskLanguage}
                />
            </PageSection>

            {category.howItWorks && (
                <PageSection id="how-it-works" pageName={category.key}>
                    <HowItWorksSection
                        heading={productGroup.howItWorksHeading}
                        contestDescription={productGroup.howItWorksContestDescription}
                        pricingLink={productGroup.howItWorksPricing}
                        categoryKey={category.key}
                        pricingOptions={productGroup.howItWorksPricingOption}
                    />
                </PageSection>
            )}

            {inspirationDesigns.length > 0 && (
                <PageSection id="inspiration" pageName={category.key}>
                    <Box
                        maxWidth={`${maxWidths.content.widescreen}px`}
                        marginLeft="auto"
                        marginRight="auto"
                        paddingY={['3bu', '3bu', '4bu', '6bu']}
                        paddingX={['1bu', '2bu', '2bu', '2bu']}
                    >
                        <DesignInspirationTabbed
                            categoryKey={category.key}
                            designs={inspirationDesigns}
                            cta={
                                <Button
                                    appearance={productGroup.heroCtaAppearance}
                                    href={productGroup.productDetailsRelativeLink}
                                    size="large"
                                    type="button"
                                    ref={primaryCTARefInspiration}
                                >
                                    {__('Get started')}
                                </Button>
                            }
                        />
                    </Box>
                </PageSection>
            )}

            <PageSection id="related-products" pageName={category.key} background={colors.grey200}>
                <RelatedProductsSection
                    ctaLink={'/categories'}
                    products={products(productGroupPrices, siteCurrency, site.siteConfig.locale)}
                    categoryKey={category.key}
                />
            </PageSection>

            <PageSection
                id="reviews"
                pageName={category.key}
                backgroundGradientColors={[colors.grey100, colors.white]}
            >
                <ReviewsSection category={category} />
            </PageSection>

            <PageSection id="faqs" pageName={category.key}>
                <FaqSection
                    faqHeading={productGroup.faqHeading}
                    faqQuestions={productGroup.faqQuestions}
                    fromPrice={getStartingPriceForProduct(
                        productGroupPrices,
                        category.key,
                        siteCurrency
                    )}
                    locale={site.siteConfig.locale}
                />
            </PageSection>

            <PageSection
                id="resources"
                pageName={category.key}
                backgroundGradientColors={[colors.grey100, colors.white]}
            >
                <ResourcesSection
                    categoryKey={category.key}
                    blogPosts={category.blogPosts}
                    cta={
                        <Button
                            appearance={productGroup.heroCtaAppearance}
                            href={productGroup.productDetailsRelativeLink}
                            size="large"
                            type="button"
                            ref={primaryCTARefResources}
                        >
                            {__('Get started')}
                        </Button>
                    }
                />
            </PageSection>
        </>
    );
}
